import { useState, useEffect } from "react";
import BookingModal from "./BookingModal";

const items = [
  { label: "handy", value: "+49 1515 3231791", cmd: "call", href: "tel:015153231791" },
  { label: "teams", value: "termin buchen", cmd: "book", booking: true },
  { label: "email", value: "saghallo@denisbitter.de", cmd: "mail", href: "mailto:saghallo@denisbitter.de" },
  { label: "standort", value: "hamburg", cmd: "locate", map: true },
];

const prompt = "$ contact --info";

const ContactInfoGrid = ({ onShowMapClick, show = true, animate = true }) => {
  const [showBooking, setShowBooking] = useState(false);
  // Phases: idle → typing → rows → done
  const [phase, setPhase] = useState(animate ? "idle" : "done");
  const [promptChars, setPromptChars] = useState(animate ? 0 : prompt.length);
  const [currentRow, setCurrentRow] = useState(animate ? 0 : items.length);
  const [rowChars, setRowChars] = useState(0);

  // Build the display string for each row
  const rowTexts = items.map((item) => `> ${item.label}    ${item.value}`);

  useEffect(() => {
    if (!animate) {
      setPhase("done");
      setPromptChars(prompt.length);
      setCurrentRow(items.length);
      setRowChars(0);
      return;
    }
    setPhase("idle");
    setPromptChars(0);
    setCurrentRow(0);
    setRowChars(0);
  }, [animate]);

  // Start typing when terminal slides in
  useEffect(() => {
    if (!animate) return;
    if (show && phase === "idle") {
      const t = setTimeout(() => setPhase("typing"), 500);
      return () => clearTimeout(t);
    }
  }, [show, phase, animate]);

  // Type the prompt
  useEffect(() => {
    if (!animate) return;
    if (phase !== "typing") return;
    if (promptChars < prompt.length) {
      const t = setTimeout(() => setPromptChars((c) => c + 1), 50);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase("rows"), 400);
    return () => clearTimeout(t);
  }, [phase, promptChars, animate]);

  // Type rows one by one, character by character
  useEffect(() => {
    if (!animate) return;
    if (phase !== "rows") return;
    if (currentRow >= items.length) {
      setPhase("done");
      return;
    }
    const text = rowTexts[currentRow];
    if (rowChars < text.length) {
      const t = setTimeout(() => setRowChars((c) => c + 1), 25);
      return () => clearTimeout(t);
    }
    // Row done — pause, then next row
    const t = setTimeout(() => {
      setCurrentRow((r) => r + 1);
      setRowChars(0);
    }, 200);
    return () => clearTimeout(t);
  }, [phase, currentRow, rowChars, rowTexts, animate]);

  const handleClick = (item) => {
    if (item.booking) setShowBooking(true);
    else if (item.map) onShowMapClick?.();
    else if (item.href) window.location.href = item.href;
  };

  return (
    <>
      <div
        className={`max-w-2xl mx-auto rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/40 font-mono transition-all duration-700 ease-out ${
          show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        }`}
      >
        {/* Title Bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#AC8E66] select-none">info</span>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-4 bg-[#0F0F0F] min-h-[60px]">
          {/* Blinking cursor before typing starts */}
          {phase === "idle" && show && (
            <p className="text-sm">
              <span className="inline-block w-[2px] h-4 bg-[#AC8E66] align-middle animate-cursor-blink" />
            </p>
          )}

          {/* Prompt typing */}
          {phase !== "idle" && (
            <p className="text-sm text-[#EFEBDC]/50 mb-3">
              {prompt.slice(0, promptChars)}
              {phase === "typing" && (
                <span className="inline-block w-[2px] h-4 bg-[#AC8E66] ml-0.5 align-middle animate-cursor-blink" />
              )}
            </p>
          )}

          {/* Rows — typed out or fully visible */}
          {items.map((item, i) => {
            // Not yet reached
            if (i > currentRow && phase !== "done") return null;
            if (i === currentRow && phase === "rows") {
              // Currently typing this row
              const text = rowTexts[i];
              const displayed = text.slice(0, rowChars);
              return (
                <div key={item.label} className="py-2.5 border-b border-[#AC8E66]/10 last:border-b-0">
                  <span className="text-sm text-[#EFEBDC]/70 whitespace-pre">{displayed}</span>
                  <span className="inline-block w-[2px] h-4 bg-[#AC8E66] ml-0.5 align-middle animate-cursor-blink" />
                </div>
              );
            }
            if (i < currentRow || phase === "done") {
              // Fully typed — interactive button
              return (
                <button
                  key={item.label}
                  onClick={() => handleClick(item)}
                  className="w-full flex items-center gap-3 py-2.5 border-b border-[#AC8E66]/10 last:border-b-0 hover:bg-[#AC8E66]/5 transition-colors text-left cursor-pointer group"
                >
                  <span className="text-[#AC8E66]/60 text-xs">&gt;</span>
                  <span className="text-xs text-[#EFEBDC]/40 w-16 shrink-0">{item.label}</span>
                  <span className="text-sm text-[#EFEBDC]/70 flex-1 group-hover:text-[#EFEBDC] transition-colors">{item.value}</span>
                  <span className="text-xs text-[#AC8E66]/30 group-hover:text-[#AC8E66]/60 transition-colors hidden sm:inline">
                    [{item.cmd}]
                  </span>
                </button>
              );
            }
            return null;
          })}
        </div>
      </div>

      {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
    </>
  );
};

export default ContactInfoGrid;
